import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SectionContainer = styled(Box)({
  padding: '140px 40px',
  background: 'linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)',
  position: 'relative',
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const GlowOrb = styled(Box)({
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(0, 212, 255, 0.06) 0%, transparent 70%)',
  top: '-20%',
  left: '-10%',
  pointerEvents: 'none'
});

const ContentWrapper = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '60px'
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  background: 'rgba(0, 212, 255, 0.1)',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  borderRadius: '100px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  color: '#00d4ff',
  textTransform: 'uppercase',
  letterSpacing: '1.5px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 48px)',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const CardsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '28px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr'
  }
});

const InfoCard = styled(Box)({
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '24px',
  padding: '36px',
  animation: `${fadeInUp} 0.8s ease-out`,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: 'rgba(0, 212, 255, 0.2)',
    background: 'rgba(255, 255, 255, 0.03)'
  }
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '28px'
});

const IconWrapper = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '26px',
    color: '#00d4ff'
  }
});

const CardTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: '24px',
  fontWeight: 600,
  color: '#ffffff'
});

const ContactList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 20px',
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  borderRadius: '14px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.04)',
    borderColor: 'rgba(0, 212, 255, 0.2)',
    transform: 'translateX(6px)',
    '& .contact-icon': {
      background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
      '& svg': {
        color: '#ffffff'
      }
    }
  }
});

const ContactIcon = styled(Box)({
  width: '42px',
  height: '42px',
  borderRadius: '12px',
  background: 'rgba(0, 212, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '20px',
    color: '#00d4ff',
    transition: 'color 0.3s ease'
  }
});

const ContactText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)'
});

const HoursCard = styled(Box)({
  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
  border: '1px solid rgba(0, 212, 255, 0.15)',
  borderRadius: '24px',
  padding: '36px',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: '0.15s',
  animationFillMode: 'backwards'
});

const HoursList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
});

const HoursRow = styled(Box)(({ isToday, isClosed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 18px',
  background: isToday ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255, 255, 255, 0.02)',
  border: isToday ? '1px solid rgba(0, 212, 255, 0.3)' : '1px solid transparent',
  borderRadius: '12px',
  transition: 'all 0.3s ease'
}));

const DayName = styled(Box)(({ isToday }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: isToday ? 600 : 500,
  color: isToday ? '#00d4ff' : '#ffffff'
}));

const TodayBadge = styled(Box)({
  padding: '4px 10px',
  background: 'linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%)',
  borderRadius: '100px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '10px',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
});

const HoursTime = styled(Box)(({ isClosed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: isClosed ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)'
}));

const defaultHours = [
  { day: 'Monday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Tuesday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Wednesday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Thursday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Friday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Saturday', open: '10:00 AM', close: '4:00 PM', isClosed: false },
  { day: 'Sunday', open: '', close: '', isClosed: true }
];

const BusinessHoursSection = ({ businessHours = [], contactInfo = {} }) => {
  const hours = businessHours.length > 0 ? businessHours : defaultHours;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <SectionContainer id="hours">
      <GlowOrb />
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <AccessTimeIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
            <BadgeText>Hours & Contact</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Visit <GradientText>Us</GradientText>
          </SectionTitle>
        </SectionHeader>

        <CardsGrid>
          <InfoCard>
            <CardHeader>
              <IconWrapper>
                <LocationOnIcon />
              </IconWrapper>
              <CardTitle>Contact Info</CardTitle>
            </CardHeader>
            <ContactList>
              {contactInfo?.address && (
                <ContactItem>
                  <ContactIcon className="contact-icon">
                    <LocationOnIcon />
                  </ContactIcon>
                  <ContactText>{contactInfo.address}</ContactText>
                </ContactItem>
              )}
              {contactInfo?.phone && (
                <ContactItem onClick={() => window.location.href = `tel:${contactInfo.phone}`}>
                  <ContactIcon className="contact-icon">
                    <PhoneIcon />
                  </ContactIcon>
                  <ContactText>{contactInfo.phone}</ContactText>
                </ContactItem>
              )}
              {contactInfo?.email && (
                <ContactItem onClick={() => window.location.href = `mailto:${contactInfo.email}`}>
                  <ContactIcon className="contact-icon">
                    <EmailIcon />
                  </ContactIcon>
                  <ContactText>{contactInfo.email}</ContactText>
                </ContactItem>
              )}
              {!contactInfo?.address && !contactInfo?.phone && !contactInfo?.email && (
                <>
                  <ContactItem>
                    <ContactIcon className="contact-icon">
                      <LocationOnIcon />
                    </ContactIcon>
                    <ContactText>123 Business Street, City</ContactText>
                  </ContactItem>
                  <ContactItem>
                    <ContactIcon className="contact-icon">
                      <PhoneIcon />
                    </ContactIcon>
                    <ContactText>(555) 123-4567</ContactText>
                  </ContactItem>
                </>
              )}
            </ContactList>
          </InfoCard>

          <HoursCard>
            <CardHeader>
              <IconWrapper>
                <AccessTimeIcon />
              </IconWrapper>
              <CardTitle>Business Hours</CardTitle>
            </CardHeader>
            <HoursList>
              {hours.map((item, index) => {
                const isToday = item.day === today;
                const isClosed = item.isClosed;
                return (
                  <HoursRow key={index} isToday={isToday} isClosed={isClosed}>
                    <DayName isToday={isToday}>
                      {item.day}
                      {isToday && <TodayBadge>Today</TodayBadge>}
                    </DayName>
                    <HoursTime isClosed={isClosed}>
                      {isClosed ? (
                        <>
                          <CancelIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                          Closed
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
                          {item.open} - {item.close}
                        </>
                      )}
                    </HoursTime>
                  </HoursRow>
                );
              })}
            </HoursList>
          </HoursCard>
        </CardsGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default BusinessHoursSection;
